import { format } from 'date-fns';
import React, { useState } from 'react';
import { useCloseWeek } from '../hooks/useCloseWeek';
import { colors } from '../theme/colors';
import type { WeeklySummary as WeeklySummaryType } from '../types';

interface WeeklySummaryProps {
  data: WeeklySummaryType[];
  isLoading?: boolean;
  employeeId?: string;
  calendarWeek?: string;
  weekStatus?: string;
  closeBlocked?: boolean;
  closeBlockedReason?: string;
}

export const WeeklySummary: React.FC<WeeklySummaryProps> = ({
  data,
  isLoading = false,
  employeeId,
  calendarWeek,
  weekStatus = 'open',
  closeBlocked = false,
  closeBlockedReason,
}) => {
  const { closeWeek, closingWeek } = useCloseWeek(employeeId);
  const isClosing = closingWeek !== null;
  const [isClosed, setIsClosed] = useState(weekStatus === 'close');
  const closeDisabled = isClosing || !employeeId || !calendarWeek || isClosed || closeBlocked;

  React.useEffect(() => {
    setIsClosed(weekStatus === 'close');
  }, [weekStatus]);

  const handleCloseHours = async () => {
    if (calendarWeek && (await closeWeek(calendarWeek))) setIsClosed(true);
  };

  const totalFteHours = data.reduce((sum, day) => sum + day.fteHours, 0);
  const totalProjectHours = data.reduce((sum, day) => sum + day.projectLoggedHours, 0);
  const totalActualHours = data.reduce((sum, day) => sum + day.actualHours, 0);
  const totalLoggedVsProject = data.reduce(
    (sum, day) => sum + (day.actualHours - day.projectLoggedHours),
    0
  );
  const totalDifference = data.reduce((sum, day) => sum + day.difference, 0);
  
  const TOLERANCE = 0.01;
  const isWithinTolerance = Math.abs(totalLoggedVsProject) < TOLERANCE;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, 'dd-MMM');
  };

  const getDifferenceColor = (difference: number) => {
    if (difference === 0) return colors.accent.green;
    if (difference > 0) return colors.accent.green;
    return colors.accent.red;
  };

  const getDifferenceSign = (difference: number) => {
    if (difference === 0) return '0.00';
    return difference > 0 ? `+${difference.toFixed(2)}` : difference.toFixed(2);
  };

  return (
    <div
      style={{
        backgroundColor: colors.neutral.white,
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5rem',
              color: colors.neutral.grey[600],
            }}
          >
            <div
              style={{
                width: '32px',
                height: '32px',
                border: `3px solid ${colors.neutral.grey[200]}`,
                borderTop: `3px solid ${colors.accent.blue}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            <p style={{ margin: 0, fontSize: '0.875rem' }}>Loading...</p>
          </div>
        </div>
      )}
      {/* Header */}
      <div
        style={{
          backgroundColor: colors.primary.background,
          color: colors.primary.text,
          padding: '1rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: '600',
          }}
        >
          Weekly Hours Summary
        </h3>
        <button
          disabled={closeDisabled}
          title={!isClosed ? closeBlockedReason : undefined}
          onClick={handleCloseHours}
          style={{
            backgroundColor: closeDisabled ? colors.neutral.grey[400] : colors.accent.blue,
            color: colors.neutral.white,
            padding: '0.5rem 1rem',
            borderRadius: '0.25rem',
            border: 'none',
            cursor: closeDisabled ? 'not-allowed' : 'pointer',
            opacity: closeDisabled ? 0.6 : 1,
          }}
        >
          {isClosing ? 'Closing...' : isClosed ? 'Week Closed' : 'Close Hours'}
        </button>
      </div>

      {/* Summary Table */}
      <div style={{ padding: '1.5rem' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `120px repeat(${data.length}, 1fr) 100px`,
            gap: '1rem',
            alignItems: 'center',
          }}
        >
          {/* Header Row */}
          <div
            style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: colors.secondary.text,
              padding: '0.75rem 0',
              borderBottom: `2px solid ${colors.neutral.grey[200]}`,
            }}
          >
            Metric
          </div>
          {data.map((day) => (
            <div
              key={day.date}
              style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: colors.secondary.text,
                padding: '0.75rem 0',
                borderBottom: `2px solid ${colors.neutral.grey[200]}`,
                textAlign: 'center',
              }}
            >
              {formatDate(day.date)}
            </div>
          ))}
          <div
            style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: colors.secondary.text,
              padding: '0.75rem 0',
              borderBottom: `2px solid ${colors.neutral.grey[200]}`,
              textAlign: 'center',
            }}
          >
            Total
          </div>

          {/* FTE Hours Row */}
          <div
            style={{
              fontSize: '0.875rem',
              fontWeight: '500',
              color: colors.secondary.text,
              padding: '0.75rem 0',
              borderBottom: `1px solid ${colors.neutral.grey[100]}`,
            }}
          >
          Planned Hours 1️⃣
          </div>
          {data.map((day) => (
            <div
              key={`fte-${day.date}`}
              style={{
                fontSize: '0.875rem',
                color: colors.secondary.text,
                padding: '0.75rem 0',
                borderBottom: `1px solid ${colors.neutral.grey[100]}`,
                textAlign: 'center',
              }}
            >
              {day.fteHours.toFixed(1)}
            </div>
          ))}
          <div
            style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: colors.secondary.text,
              padding: '0.75rem 0',
              borderBottom: `1px solid ${colors.neutral.grey[100]}`,
              textAlign: 'center',
            }}
          >
            {totalFteHours.toFixed(1)}
          </div>

          {/* Project Logged Hours Row */}
          <div
            style={{
              fontSize: '0.875rem',
              fontWeight: '500',
              color: colors.secondary.text,
              padding: '0.75rem 0',
              borderBottom: `1px solid ${colors.neutral.grey[100]}`,
            }}
          >
            Project Hours 2️⃣
          </div>
          {data.map((day) => (
            <div
              key={`project-${day.date}`}
              style={{
                fontSize: '0.875rem',
                color: colors.secondary.text,
                padding: '0.75rem 0',
                borderBottom: `1px solid ${colors.neutral.grey[100]}`,
                textAlign: 'center',
              }}
            >
              {day.projectLoggedHours.toFixed(2)}
            </div>
          ))}
          <div
            style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: colors.secondary.text,
              padding: '0.75rem 0',
              borderBottom: `1px solid ${colors.neutral.grey[100]}`,
              textAlign: 'center',
            }}
          >
            {totalProjectHours.toFixed(2)}
          </div>

          {/* Actual Hours Row */}
          <div
            style={{
              fontSize: '0.875rem',
              fontWeight: '500',
              color: colors.secondary.text,
              padding: '0.75rem 0',
              borderBottom: `1px solid ${colors.neutral.grey[100]}`,
            }}
          >
            Actual Hours 3️⃣
          </div>
          {data.map((day) => (
            <div
              key={`actual-${day.date}`}
              style={{
                fontSize: '0.875rem',
                color: colors.secondary.text,
                padding: '0.75rem 0',
                borderBottom: `1px solid ${colors.neutral.grey[100]}`,
                textAlign: 'center',
              }}
            >
              {day.actualHours.toFixed(2)}
            </div>
          ))}
          <div
            style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: colors.secondary.text,
              padding: '0.75rem 0',
              borderBottom: `1px solid ${colors.neutral.grey[100]}`,
              textAlign: 'center',
            }}
          >
            {totalActualHours.toFixed(2)}
          </div>

          {/* Logged Hours - Project Hours Row */}
          <div
            style={{
              fontSize: '0.875rem',
              fontWeight: '500',
              color: colors.secondary.text,
              padding: '0.75rem 0',
              borderBottom: `1px solid ${colors.neutral.grey[100]}`,
            }}
          >
            Actual Hours to Project Hours:
            <br />
            3️⃣ - 2️⃣
          </div>
          {data.map((day) => {
            const loggedVsProject = day.actualHours - day.projectLoggedHours;
            return (
              <div
                key={`logged-project-${day.date}`}
                style={{
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: getDifferenceColor(loggedVsProject),
                  padding: '0.75rem 0',
                  borderBottom: `1px solid ${colors.neutral.grey[100]}`,
                  backgroundColor:
                    loggedVsProject !== 0 ? colors.accent.yellow + '40' : 'transparent',
                  textAlign: 'center',
                }}
              >
                {getDifferenceSign(loggedVsProject)}
              </div>
            );
          })}
          <div
            style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: getDifferenceColor(totalLoggedVsProject),
              padding: '0.75rem 0',
              borderBottom: `1px solid ${colors.neutral.grey[100]}`,
              backgroundColor:
                !isWithinTolerance ? colors.accent.yellow + '40' : 'transparent',
              textAlign: 'center',
            }}
          >
            {getDifferenceSign(totalLoggedVsProject)}
          </div>

          {/* Difference Row */}
          <div
            style={{
              fontSize: '0.875rem',
              fontWeight: '500',
              color: colors.secondary.text,
              padding: '0.75rem 0',
            }}
          >
            Flex Hours:
            <br />
            1️⃣ - 3️⃣
          </div>
          {data.map((day) => (
            <div
              key={`diff-${day.date}`}
              style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: getDifferenceColor(day.difference),
                padding: '0.75rem 0',
                textAlign: 'center',
                borderRadius: day.difference !== 0 ? '4px' : '0',
              }}
            >
              {getDifferenceSign(day.difference)}
            </div>
          ))}
          <div
            style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: getDifferenceColor(totalDifference),
              padding: '0.75rem 0',
              textAlign: 'center',
              borderRadius: totalDifference !== 0 ? '4px' : '0',
            }}
          >
            {getDifferenceSign(totalDifference)}
          </div>
        </div>
      </div>
    </div>
  );
};
